import {
  promiseState,
  useBooleanState,
  useDataStream,
  useNavigator,
  useStreamValue
} from 'annotation-tools/utils';
import React, { FC, memo, useCallback, useState } from 'react';
import { switchMap } from 'rxjs/operators';
import { viewTexts } from '../../../constants/view-texts';
import { useListFiltered } from '../../../functions/hooks/use-list-filtered';
import {
  ListWithPromiseState,
  listWithPromiseStateInit
} from '../../../global-state/database/common/list-with-status';
import { companyListOf } from '../../../global-state/database/company-list';
import { koumutenId$ } from '../../../global-state/router-id';
import { useLoadingPage } from '../../../global-state/show-loading-page';
import { routes } from '../../../routing/routes';
import { ICompanyType } from '../../../types/record/company';
import { NotFoundTemplate } from '../../shared/not-found-page-template';
import { CompanyListView } from './company-list-view';

export const CompanyList: FC<{}> = memo(() => {
  const navigator = useNavigator();

  const companyList$ = useDataStream<ListWithPromiseState<ICompanyType>>(
    listWithPromiseStateInit<ICompanyType>(),
    koumutenId$.pipe(switchMap(companyListOf))
  );

  const [
    createNewCompanyDialogIsOpen,
    openCreateNewCompanyDialog,
    closeCreateNewCompanyDialog
  ] = useBooleanState(false);

  const [
    editCompanyDialogIsOpen,
    openEditCompanyDialog,
    closeEditCompanyDialog
  ] = useBooleanState(false);

  const [
    deleteCompanyDialogIsOpen,
    openDeleteCompanyDialog,
    closeDeleteCompanyDialog
  ] = useBooleanState(false);

  const [
    // dummy comment to control prettier
    companyToEdit,
    setCompanyToEdit
  ] = useState<ICompanyType | undefined>(undefined);

  const [
    // dummy comment to control prettier
    companyToDelete,
    setCompanyToDelete
  ] = useState<ICompanyType | undefined>(undefined);

  /* event handlers */

  const koumutenId = useStreamValue(koumutenId$);

  const companyItemOnClick = useCallback(
    (companyId: number) => {
      if (koumutenId === undefined) return;
      navigator(routes.projectList(koumutenId, companyId));
    },
    [navigator, koumutenId]
  );

  const editCompanyButtonOnClick = useCallback(
    (company: ICompanyType) => {
      setCompanyToEdit(company);
      openEditCompanyDialog();
    },
    [setCompanyToEdit, openEditCompanyDialog]
  );

  const deleteCompanyButtonOnClick = useCallback(
    (company: ICompanyType) => {
      setCompanyToDelete(company);
      openDeleteCompanyDialog();
    },
    [setCompanyToDelete, openDeleteCompanyDialog]
  );

  const {
    searchString,
    onSearchStringChange,
    page,
    onPageChange,
    rowsPerPage,
    setRowsPerPage: onRowsPerPageChange,
    numRows,
    listFilteredCurrentPage: companyListFiltered
  } = useListFiltered(companyList$);

  useLoadingPage(companyListFiltered?.status === 'pending');

  return (
    <>
      {companyListFiltered === undefined ? (
        undefined
      ) : companyListFiltered.status === promiseState.error ? (
        <NotFoundTemplate message={viewTexts.notFoundMessages.companyList} />
      ) : (
        <CompanyListView
          searchString={searchString}
          onSearchStringChange={onSearchStringChange}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          numRows={numRows}
          companyListFiltered={companyListFiltered.value.latest}
          companyOnClick={companyItemOnClick}
          createNewCompanyOnClick={openCreateNewCompanyDialog}
          createNewCompanyDialogOpen={createNewCompanyDialogIsOpen}
          createNewCompanyDialogOnClose={closeCreateNewCompanyDialog}
          editCompanyOnClick={editCompanyButtonOnClick}
          editCompanyDialogIsOpen={editCompanyDialogIsOpen}
          editCompanyDialogOnClose={closeEditCompanyDialog}
          companyToEdit={companyToEdit}
          deleteCompanyOnClick={deleteCompanyButtonOnClick}
          deleteCompanyDialogIsOpen={deleteCompanyDialogIsOpen}
          deleteCompanyDialogOnClose={closeDeleteCompanyDialog}
          companyToDelete={companyToDelete}
        />
      )}
    </>
  );
});

CompanyList.displayName = 'CompanyList';
